import { useEffect, useMemo, useState } from "react";
import { Image, ScrollView, Text, View } from "react-native";
import { CircleAlert, Minus, Plus } from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  IconButton,
  PrimaryButton,
  Row,
  SectionTitleRow,
  colors,
  semanticColors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { useCareTeam } from "@src/packages/care/hooks/useCareTeam";
import {
  useCareConnections,
  useUpdateCareConnection,
  type CareConnection,
} from "@src/packages/patients/hooks/useCareConnections";
import { getDevCareConnectionsOverride } from "@src/packages/patients/dev/devCareConnections";
import { EMBER } from "../../../../onboarding/theme";
import CareConnectionModal from "../../YouScreen/modals/CareConnectionModal";
import {
  CARE_CONNECTION_ICONS,
  TINTED_ICON_FILTER,
} from "../../YouScreen/icons";

type Props = {
  visible: boolean;
  cycleId: string | undefined;
  clinicName?: string | null;
  onClose: () => void;
  /** Opens the patient's full care team, in this cycle's context. */
  onOpenCareTeam: () => void;
};

const CARE_TEAM_ICON =
  require("../../../../assets/images/treatment-schedule.png") as number;

const ROW_TITLE_STYLE = {
  fontSize: 14,
  lineHeight: 18,
  fontWeight: "500" as const,
  color: EMBER.colors.text,
};
const ROW_SUBTITLE_STYLE = {
  fontSize: 12,
  lineHeight: 16,
  color: EMBER.colors.textLight,
};

/**
 * One cycle's care team. The providers come from the patient's canonical
 * care-team records: adding or removing one only changes the record's
 * `cycleIds`, the same field the provider form's "Include in this cycle"
 * switch writes. Tapping a provider, or adding a new one, opens that same
 * provider form over this screen and comes back here when it closes. The
 * clinic is the cycle's own clinic, and clinic-assigned professionals are
 * shown but not edited here.
 */
export default function CycleCareTeamModal({
  visible,
  cycleId,
  clinicName,
  onClose,
  onOpenCareTeam,
}: Props) {
  const t = useTranslations();
  const { data: officialMembers = [] } = useCareTeam(cycleId);
  const updateCareConnection = useUpdateCareConnection();
  const { data: fetchedCareConnections = [] } = useCareConnections();
  const careConnections =
    getDevCareConnectionsOverride() ?? fetchedCareConnections;
  const [actionError, setActionError] = useState<"add" | "remove" | null>(null);

  /** The provider form open over this dialog; `null` id is a new provider. */
  const [editing, setEditing] = useState<{
    connectionId: string | null;
  } | null>(null);

  useEffect(() => {
    if (!visible) return;
    setActionError(null);
    setEditing(null);
  }, [visible]);

  // Only providers join a cycle through cycleIds. The cycle's clinic has its
  // own record, so clinic and pharmacy records are not offered here.
  const providers = useMemo(
    () =>
      careConnections.filter((connection) => connection.kind === "provider"),
    [careConnections],
  );
  const inCycle = providers.filter(
    (provider) => !!cycleId && provider.cycleIds.includes(cycleId),
  );
  const available = providers.filter(
    (provider) => !cycleId || !provider.cycleIds.includes(cycleId),
  );

  const setInCycle = async (provider: CareConnection, add: boolean) => {
    if (!cycleId) return;
    setActionError(null);
    try {
      await updateCareConnection.mutateAsync({
        connectionId: provider.id,
        body: {
          cycleIds: add
            ? Array.from(new Set([...provider.cycleIds, cycleId]))
            : provider.cycleIds.filter((id) => id !== cycleId),
        },
      });
    } catch {
      setActionError(add ? "add" : "remove");
    }
  };

  const isBusy = updateCareConnection.isPending;
  const describe = (provider: CareConnection) =>
    [provider.role, provider.practiceName].filter(Boolean).join(" · ") ||
    t("you.connections_sharing.care_team_member");

  const rowStyle = (index: number) => ({
    borderRadius: 0,
    borderTopWidth: index > 0 ? 1 : 0,
    borderTopColor: colors.border,
  });

  const renderProvider = (
    provider: CareConnection,
    index: number,
    tap: "add" | "remove",
  ) => (
    <Row
      key={provider.id}
      surface="plain"
      leading={
        <Image
          source={CARE_CONNECTION_ICONS.provider}
          resizeMode="contain"
          style={{ width: 24, height: 24, ...TINTED_ICON_FILTER }}
        />
      }
      leadingSize={24}
      title={provider.name}
      subtitle={describe(provider)}
      onPress={() => setEditing({ connectionId: provider.id })}
      chevron={false}
      action="cycle_care_team_open_provider"
      accessibilityLabel={provider.name}
      trailing={
        tap === "add" ? (
          <IconButton
            accessibilityLabel={t(
              "care.cycle_clinic_team.add_person_to_this_cycle",
              { params: { name: provider.name } },
            )}
            action="cycle_care_team_add_provider"
            onPress={() => void setInCycle(provider, true)}
            disabled={isBusy}
            size={32}
            surface="soft"
          >
            <Plus size={16} color={EMBER.colors.textLight} strokeWidth={1.8} />
          </IconButton>
        ) : (
          <IconButton
            accessibilityLabel={t(
              "care.cycle_clinic_team.remove_person_from_this_cycle",
              { params: { name: provider.name } },
            )}
            action="cycle_care_team_remove_provider"
            onPress={() => void setInCycle(provider, false)}
            disabled={isBusy}
            size={32}
            surface="soft"
          >
            <Minus size={16} color={EMBER.colors.textLight} strokeWidth={1.8} />
          </IconButton>
        )
      }
      gap={10}
      paddingHorizontal={10}
      paddingVertical={10}
      borderRadius={0}
      titleNumberOfLines={1}
      subtitleNumberOfLines={1}
      titleStyle={ROW_TITLE_STYLE}
      subtitleStyle={ROW_SUBTITLE_STYLE}
      style={rowStyle(index)}
    />
  );

  const hasCycleTeam =
    !!clinicName || inCycle.length > 0 || officialMembers.length > 0;

  return (
    <>
      <Dialog
        visible={visible && editing === null}
        onClose={() => {
          if (!isBusy) onClose();
        }}
        layout="centered"
        closeButton="minimal"
        dismissOnBackdropTap={!isBusy}
        contentStyle={{
          paddingTop: 28,
          paddingHorizontal: 24,
          paddingBottom: 24,
        }}
        testID="cycle-care-team-modal"
      >
        <DialogIcon source={CARE_TEAM_ICON} width={50} height={58} />
        <DialogHeader
          align="center"
          title={t("care.cycle_clinic_team.manage_this_cycles_care_team")}
          subtitle={t(
            "care.cycle_clinic_team.removing_keeps_them_on_care_team",
          )}
          style={{ marginTop: 8, marginBottom: 18 }}
        />

        <ScrollView
          style={{ maxHeight: 400, marginBottom: 16 }}
          showsVerticalScrollIndicator={false}
        >
          {clinicName ? (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                paddingHorizontal: 12,
                paddingVertical: 12,
                borderRadius: 12,
                backgroundColor: semanticColors.action.quiet.background,
                marginBottom: 8,
              }}
            >
              <Image
                source={CARE_CONNECTION_ICONS.clinic}
                resizeMode="contain"
                style={{ width: 24, height: 24, ...TINTED_ICON_FILTER }}
              />
              <View style={{ flex: 1 }}>
                <Text
                  className="font-dm-sans"
                  numberOfLines={1}
                  style={ROW_TITLE_STYLE}
                >
                  {clinicName}
                </Text>
                <Text className="font-dm-sans" style={ROW_SUBTITLE_STYLE}>
                  {t("care.cycle_clinic_team.clinic_for_this_cycle")}
                </Text>
              </View>
            </View>
          ) : null}

          {inCycle.length > 0 || officialMembers.length > 0 ? (
            <View
              style={{
                overflow: "hidden",
                borderRadius: 12,
                backgroundColor: semanticColors.action.quiet.background,
              }}
            >
              {inCycle.map((provider, index) =>
                renderProvider(provider, index, "remove"),
              )}
              {officialMembers.map((member, index) => (
                <Row
                  key={member.id}
                  surface="plain"
                  leading={
                    <Image
                      source={CARE_CONNECTION_ICONS.provider}
                      resizeMode="contain"
                      style={{ width: 24, height: 24, ...TINTED_ICON_FILTER }}
                    />
                  }
                  leadingSize={24}
                  title={member.name}
                  subtitle={[
                    member.role,
                    t("care.cycle_clinic_team.assigned_by_your_clinic"),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  gap={10}
                  paddingHorizontal={10}
                  paddingVertical={10}
                  borderRadius={0}
                  titleNumberOfLines={1}
                  subtitleNumberOfLines={1}
                  titleStyle={ROW_TITLE_STYLE}
                  subtitleStyle={ROW_SUBTITLE_STYLE}
                  style={rowStyle(inCycle.length + index)}
                />
              ))}
            </View>
          ) : null}

          {!hasCycleTeam ? (
            <View style={{ alignItems: "center", paddingVertical: 18 }}>
              <CircleAlert
                size={22}
                color={EMBER.colors.textFaint}
                strokeWidth={1.7}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  lineHeight: 20,
                  color: EMBER.colors.textLight,
                  textAlign: "center",
                  marginTop: 8,
                }}
              >
                {t("care.cycle_clinic_team.nobody_in_this_cycle_yet")}
              </Text>
            </View>
          ) : null}

          {actionError === "remove" ? (
            <View style={{ marginTop: 10 }}>
              <ActionBanner
                variant="important"
                message={t(
                  "care.cycle_clinic_team.could_not_remove_from_cycle",
                )}
              />
            </View>
          ) : null}

          <View style={{ marginTop: 18 }}>
            <SectionTitleRow
              title={t("care.cycle_clinic_team.add_from_your_care_team")}
              action={{
                label: t("care.view_all"),
                accessibilityLabel: t("care.cycle_clinic_team.open_care_team"),
                onPress: onOpenCareTeam,
                disabled: isBusy,
              }}
            />
            {available.length > 0 ? (
              <View
                style={{
                  overflow: "hidden",
                  borderRadius: 12,
                  backgroundColor: semanticColors.action.quiet.background,
                }}
              >
                {available.map((provider, index) =>
                  renderProvider(provider, index, "add"),
                )}
              </View>
            ) : (
              <Text
                className="font-dm-sans"
                style={{ ...ROW_SUBTITLE_STYLE, fontSize: 13, lineHeight: 18 }}
              >
                {providers.length > 0
                  ? t("care.cycle_clinic_team.everyone_is_in_this_cycle")
                  : t("care.cycle_clinic_team.no_providers_yet")}
              </Text>
            )}
            {actionError === "add" ? (
              <View style={{ marginTop: 10 }}>
                <ActionBanner
                  variant="important"
                  message={t("care.cycle_clinic_team.could_not_add_to_cycle")}
                />
              </View>
            ) : null}
          </View>
        </ScrollView>

        <DialogActions style={{ marginTop: 0 }}>
          <PrimaryButton
            label={t("care.cycle_clinic_team.add_a_provider")}
            action="cycle_care_team_add_new_provider"
            onPress={() => setEditing({ connectionId: null })}
            disabled={isBusy}
          />
        </DialogActions>
      </Dialog>
      <CareConnectionModal
        visible={visible && editing !== null}
        kind="provider"
        cycleId={cycleId}
        connections={providers}
        selectedConnectionId={editing?.connectionId ?? null}
        startWithNewForm={editing !== null && editing.connectionId === null}
        closeWhenDone
        icon={CARE_CONNECTION_ICONS.provider}
        onClose={() => setEditing(null)}
      />
    </>
  );
}
